import Radium from '@flipdish/radium';
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { hashHistory, Link } from 'react-router';
import {
  Icon,
  PageTitle,
  SectionHeader,
  SectionHeaderIcon,
  SectionHeaderText
} from '../../../ui';
import * as Api from '../../Api';
import * as Data from '../../Data';
import * as Store from '../../Store';
import { EnsureVisible } from '../../Utilities';
import LoadingPanel from '../LoadingPanel';
const tinycolor = require('tinycolor2');

interface Route {
  params: {
    location: number; // delivery location ID.
  };
}

interface StateProps {
  AppConfig: Data.Async<Api.AppConfig>;
  deliveryOption?: Data.Async<Store.DeliveryOption>; // keyed by delivery location id
  deliveryRestaurants: Store.DeliveryMap;
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
  translate: any;
}

type Props = StateProps & Route;

interface State {}

class DeliveryList extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {};
  }

  public componentDidMount() {
    const domNode = findDOMNode(this);
    if (domNode) {
      EnsureVisible(domNode);
    }
  }

  public urlFor = (r: Api.DeliveryRestaurantSummary): string => {
    return (
      '/restaurant/' +
      r.VirtualRestaurantId +
      '/delivery/' +
      this.props.params.location
    );
  };

  public restaurantButton = (r: Api.DeliveryRestaurantSummary): JSX.Element => {
    const { AppDesignScheme, isDark, translate } = this.props;

    let address = 'Loading';

    const detail = this.props.deliveryRestaurants[
      Store.DeliveryKey(r.VirtualRestaurantId, this.props.params.location)
    ];

    if (detail && detail.status === 'success') {
      const {
        data: { PhysicalRestaurantAddress }
      } = detail;
      address = PhysicalRestaurantAddress;
    }

    const styles = {
      cell: {
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .setAlpha(0.2)
          .toString(),
        ':hover': {
          borderColor: AppDesignScheme.Secondary,
          boxShadow: `-2px 2px 9px -5px ${AppDesignScheme.Secondary}`,
          backgroundColor: tinycolor(AppDesignScheme.Primary)
            .darken(3)
            .toString()
        },
        ':active': {
          borderColor: AppDesignScheme.Secondary,
          boxShadow: `-2px 2px 9px -5px ${AppDesignScheme.Secondary}`,
          backgroundColor: tinycolor(AppDesignScheme.Tertiary)
            .setAlpha(0.1)
            .toString()
        }
      },
      darkCell: {
        borderColor: AppDesignScheme.Primary,
        ':hover': {
          backgroundColor: 'transparent',
          borderColor: AppDesignScheme.Secondary,
          boxShadow: 'none'
        },
        ':active': {
          borderColor: AppDesignScheme.Secondary,
          boxShadow: `-2px 2px 9px -5px ${AppDesignScheme.Secondary}`,
          backgroundColor: tinycolor(AppDesignScheme.Tertiary)
            .lighten(10)
            .toString()
        }
      },
      primary: {
        color: AppDesignScheme.Primary
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      }
    };

    return (
      <Link key={r.VirtualRestaurantId} to={this.urlFor(r)}>
        <div
          key={`delivery-${r.VirtualRestaurantId}`}
          className="delivery__cell"
          style={isDark ? styles.darkCell : styles.cell}
        >
          <p
            className="tertiary clear-margins fd-h4"
            style={
              Radium.getState(
                this.state,
                `delivery-${r.VirtualRestaurantId}`,
                ':active'
              ) ||
              Radium.getState(
                this.state,
                `delivery-${r.VirtualRestaurantId}`,
                ':hover'
              )
                ? styles.secondary
                : isDark
                ? styles.primary
                : styles.tertiary
            }
          >
            {translate('Order_delivery_from')}
          </p>
          <p className="secondary clear-margins fd-h3" style={styles.secondary}>
            {r.Name}
          </p>
          {address ? (
            <p
              className="tertiary clear-margins fd-h4"
              style={
                Radium.getState(
                  this.state,
                  `delivery-${r.VirtualRestaurantId}`,
                  ':active'
                ) ||
                Radium.getState(
                  this.state,
                  `delivery-${r.VirtualRestaurantId}`,
                  ':hover'
                )
                  ? styles.secondary
                  : isDark
                  ? styles.primary
                  : styles.tertiary
              }
            >
              {address}
            </p>
          ) : null}
        </div>
      </Link>
    );
  };

  public render(): JSX.Element | null {
    const { deliveryOption, AppDesignScheme, isDark, translate } = this.props;

    if (!deliveryOption || deliveryOption.status !== 'success') {
      return <LoadingPanel AppDesignScheme={AppDesignScheme} isDark={isDark} />;
    }

    if (deliveryOption.data.type === 'skip') {
      hashHistory.replace(
        '/restaurant/' +
          deliveryOption.data.to.VirtualRestaurantId +
          '/delivery/' +
          this.props.params.location
      );
      return null;
    }

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      nav: {
        ':hover': {},
        ':active': {}
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      }
    };

    return (
      <div className="container">
        <div className="row delivery">
          <div className="col-md-12">
            <div style={{ marginTop: '20px' }} />

            <PageTitle>
              {translate('Select_a_restaurant_for_delivery')}
            </PageTitle>

            <Link to="/where" data-fd="delivery-back">
              <div
                key="fd-nav"
                className="fd-nav flex flex-align-center"
                style={styles.nav as any}
              >
                <i
                  className="arrow_carrot-left tertiary"
                  aria-hidden="true"
                  style={
                    Radium.getState(this.state, 'fd-nav', ':active')
                      ? styles.secondaryActive
                      : Radium.getState(this.state, 'fd-nav', ':hover')
                      ? styles.secondaryHover
                      : isDark
                      ? styles.primary
                      : styles.tertiary
                  }
                />
                <p
                  className="inline-block clear-margins tertiary bold"
                  style={
                    Radium.getState(this.state, 'fd-nav', ':active')
                      ? styles.secondaryActive
                      : Radium.getState(this.state, 'fd-nav', ':hover')
                      ? styles.secondaryHover
                      : isDark
                      ? styles.primary
                      : styles.tertiary
                  }
                >
                  <u>{translate('Select_a_different_location')}</u>
                </p>
              </div>
            </Link>
          </div>
          <div className="col-md-8 col-lg-8 col-sm-12">
            <SectionHeader>
              <SectionHeaderIcon>
                <Icon
                  fontSize="large"
                  aria-hidden
                  color="inherit"
                  className="ei icon_pin"
                />
              </SectionHeaderIcon>

              <SectionHeaderText
                title={translate('Delivery')}
                subTitle={translate('Deliver_to')}
              />
            </SectionHeader>

            {deliveryOption.data.of.map(this.restaurantButton)}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: Store.State, ownProps: Route): StateProps => {
  return {
    AppConfig: state.flipdish.AppConfig,
    deliveryRestaurants: state.deliveryRestaurants,
    deliveryOption: state.deliveryOptions[ownProps.params.location],
    AppDesignScheme: state.appDesignScheme,
    isDark: state.flipdish.isDark,
    translate: getTranslate(state.locale)
  };
};

export default connect(mapStateToProps)(Radium(DeliveryList));
